<template>
  <listing-layout>
    <div slot="heading" class="d-flex flex-wrap justify-content-between align-items-center mb-3">
      <page-heading class="mb-0">Drafts</page-heading>
      <router-link
        :to="{ name: 'Create Draft' }"
        class="btn btn-primary d-md-none px-xl-5 mt-2 mt-sm-0"
      >Create<span class="d-none d-sm-inline"> Draft</span></router-link>
    </div>

    <div slot="content">
      <div class="alert alert-success" v-if="alertMessage">{{ alertMessage }}</div>
      <div class="alert alert-warning" v-if="errorMessage">{{ errorMessage }}</div>

      <filter-bar :active="hasActiveFilters" @clear="clearAll">
        <div class="filter-field">
          <span class="filter-field__label">Date&nbsp;<small>(from)</small></span>
          <div class="filter-field__control">
            <input
              type="date"
              class="form-control form-date"
              :max="filters.to_date || null"
              :value="filters.from_date"
              @change="setFilter('from_date', $event.target.value)"
            >
          </div>
        </div>
        <div class="filter-field">
          <span class="filter-field__label">Date&nbsp;<small>(to)</small></span>
          <div class="filter-field__control">
            <input
              type="date"
              class="form-control form-date"
              :min="filters.from_date || null"
              :value="filters.to_date"
              @change="setFilter('to_date', $event.target.value)"
            >
          </div>
        </div>
        <filter-multiselect
          label="Zone"
          option-label="code"
          placeholder="Any zone"
          :value="filters.zone_id"
          :options="zoneOptions"
          @input="ids => setFilter('zone_id', ids)"
          @clear="clearFilter('zone_id')"
        />
        <filter-checkbox
          label="Unpublished"
          :value="filters.unpublished"
          @input="onUnpublishedToggle"
        />
        <filter-checkbox
          label="Deleted"
          :value="filters.deleted"
          @input="onDeletedToggle"
        />
        <filter-checkbox
          label="Open for Nominations"
          :value="filters.open_for_nominations"
          @input="onOpenForNominationsToggle"
        />
      </filter-bar>

      <div class="form-inline mb-sm-3 d-flex justify-content-between">
        <div class="d-flex col-12 col-md-auto px-0 mb-3 mb-md-0">
          <label class="sr-only">Filter</label>
          <div class="filter flex-wrap">
            <div class="form-check pl-0" v-for="tab in tabs" :key="tab.value">
              <input
                class="form-check-input"
                type="radio"
                name="draftTabFilter"
                :id="'draftTab-' + tab.value"
                :value="tab.value"
                :checked="filters.tab === tab.value"
                @change="onTabChange(tab.value)"
              >
              <label class="form-check-label" :for="'draftTab-' + tab.value">{{ tab.label }}</label>
            </div>
          </div>
        </div>

        <div class="form-group col-12 col-lg-auto mt-2 mt-lg-0 ml-auto px-0 d-flex align-items-center">
          <label for="draftsSearch" class="d-none d-lg-inline mr-xl-2 mb-0 pr-2 font-weight-normal">Search</label>
          <div class="input-append d-flex flex-grow-1 flex-md-grow-0 mr-md-2">
            <input
              id="draftsSearch"
              v-model="searchInput"
              type="text"
              class="form-control mb-0 flex-grow-1 flex-md-grow-0"
              @keyup.enter="applySearch"
              @search="applySearch"
            >
            <svg
              class="append"
              role="button"
              aria-label="Search"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 14 14"
              width="16"
              height="16"
              @click="applySearch"
            >
              <rect width="100%" height="100%" fill="none" />
              <g>
                <path fill="#231815" d="M5.5 11C2.5 11 0 8.5 0 5.5S2.5 0 5.5 0 11 2.5 11 5.5 8.5 11 5.5 11zm0-10C3 1 1 3 1 5.5S3 10 5.5 10 10 8 10 5.5 8 1 5.5 1z" />
                <path fill="#231815" d="M9.5 8.8l4.4 4.4c.2.2.2.5 0 .7l-.4.1-.4-.1-4.4-4.4.8-.7z" />
              </g>
            </svg>
          </div>
          <router-link
            :to="{ name: 'Create Draft' }"
            class="btn btn-fz-sm btn-primary d-none d-md-block px-xl-5 mt-2 mt-sm-0 ml-md-0"
          >Create<span class="d-inline"> Draft</span></router-link>
        </div>
      </div>

      <div class="position-relative" style="min-height: 240px">
        <responsive-table
          :columns="columns"
          :rows="rows"
          :loading="loading"
          :breakpoints="breakpoints"
          :sort="filters.sort"
          :direction="filters.direction"
          empty-text="No drafts found."
          @sort="setSort"
        >
          <template #cell:name="{ row }">
            <list-name-cell align="right" :label="row.name" :to="{ name: 'Events', params: { draftId: String(row.id) } }">
              <template #badges>
                <span
                  v-if="row.draft_status_id && row.draft_status_label"
                  class="badge dl-status-badge"
                  :class="statusBadgeClass(row.draft_status_id)"
                >
                  <svg
                    v-if="row.points_status === 'finalised' && isPublishedStatus(row.draft_status_id)"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 512 512"
                    width="12"
                    class="text-success"
                  ><path fill="currentColor" d="M173.898 439.404l-166.4-166.4c-9.997-9.997-9.997-26.206 0-36.204l36.203-36.204c9.997-9.998 26.207-9.998 36.204 0L192 312.69 432.095 72.596c9.997-9.997 26.207-9.997 36.204 0l36.203 36.204c9.997 9.997 9.997 26.206 0 36.204l-294.4 294.401c-9.998 9.997-26.207 9.997-36.204-.001z" /></svg>
                  {{ row.draft_status_label }}
                </span>
                <span v-if="row.deleted" class="badge alert-secondary">Deleted</span>
                <span
                  v-if="filters.open_for_nominations === '1' && nominationStageBadge(row)"
                  class="badge"
                  :class="nominationStageBadge(row).badgeClass"
                >{{ nominationStageBadge(row).label }}</span>
              </template>
            </list-name-cell>
          </template>

          <template #cell:committee="{ row }">
            <span class="dl-committee">
              <router-link
                v-if="row.committee_id"
                :to="{ name: 'Committee Profile', params: { id: String(row.committee_id) } }"
                class="text-primary dl-committee__name"
              >{{ row.committee_name }}</router-link>
              <span v-else class="dl-committee__name">{{ row.committee_name }}</span>
              <committee-secretaries-link :committee-name="row.committee_name" />
            </span>
          </template>

          <template #cell:zone="{ row }">
            <span class="dl-zone">
              <span>{{ row.zone_code }}</span>
              <span v-if="row.is_final" class="badge alert-warning">Final</span>
            </span>
          </template>

          <template #cell:start_date="{ row }">{{ formatDmy(row.start_date) }}</template>
          <template #cell:end_date="{ row }">{{ formatDmy(row.end_date) }}</template>

          <template #cell:actions="{ row }">
            <div class="actions__wrap">
              <template v-if="!row.deleted">
                <app-tooltip text="Edit Draft">
                  <span class="d-inline-flex align-items-center justify-content-center" style="width: 24px; height: 24px;">
                    <router-link :to="{ name: 'Edit Draft', params: { draftId: String(row.id) } }" class="icon-btn icon-btn--edit" />
                  </span>
                </app-tooltip>
                <app-tooltip text="View Events">
                  <span class="d-inline-flex align-items-center justify-content-center" style="width: 24px; height: 24px;">
                    <router-link :to="{ name: 'Events', params: { draftId: String(row.id) } }" class="icon-btn icon-btn--view" />
                  </span>
                </app-tooltip>
                <app-tooltip text="Delete Draft" align="right">
                  <span class="d-inline-flex align-items-center justify-content-center" style="width: 24px; height: 24px;">
                    <button class="btn-actions btn-delete" @click="askDelete(row)" />
                  </span>
                </app-tooltip>
                <app-tooltip
                  v-if="Number(row.has_settings) === 1 && Number(row.is_nominations_published) === 0"
                  text="Publish draft"
                  align="right"
                >
                  <span class="d-inline-flex align-items-center justify-content-center" style="width: 24px; height: 24px;">
                    <button class="icon-btn icon-btn--publish" :disabled="publishingId === row.id" @click="publishDraft(row)" />
                  </span>
                </app-tooltip>
              </template>
              <app-tooltip v-else text="Restore Draft">
                <span class="d-inline-flex align-items-center justify-content-center" style="width: 24px; height: 24px;">
                  <button class="icon-btn icon-btn--restore" :disabled="restoringId === row.id" @click="restoreDraftRow(row)" />
                </span>
              </app-tooltip>
            </div>
          </template>
        </responsive-table>
      </div>

      <!-- Same markup/classes as the legacy DataTable footer (DataTableFields/ShowEntries.vue) -->
      <div class="form-inline justify-content-between align-items-start mt-3">
        <div class="d-flex align-items-center justify-content-center justify-content-md-start form-group mb-3 mb-md-0 col-12 col-md-auto px-0">
          <label for="draftsShow" class="mr-2 mb-0 font-weight-normal">Show</label>
          <select
            id="draftsShow"
            class="form-control mr-2"
            :value="filters.per_page"
            @change="setPerPage(Number($event.target.value))"
          >
            <option v-for="n in perPageOptions" :key="n" :value="n">{{ n }}</option>
          </select>
          <span>Entries</span>
        </div>

        <div class="pagination-container form-group align-items-start justify-content-center mb-3 mb-md-0" v-show="totalPages > 1">
          <pagination
            :total-pages="totalPages"
            :page="page"
            :on-change="setPage"
            :disabled="loading"
          />
        </div>
      </div>

      <modal v-if="draftToDelete" @close="draftToDelete = null">
        <div slot="body">
          <div class="alert alert-warning text-center">
            You are about to delete this draft. It will move to the Deleted tab and can be restored later. Are you sure?
          </div>
          <div class="d-flex justify-content-center">
            <button class="btn btn-outline-primary" @click="draftToDelete = null">Cancel</button>
            <button class="btn btn-primary ml-2" @click="confirmDelete">Delete</button>
          </div>
        </div>
      </modal>

      <action-gate-modal
        v-if="gateVisible"
        :error="gateError"
        :loading="gateLoading"
        @confirm="submitGatePassword"
        @cancel="cancelGate"
      />
    </div>
  </listing-layout>
</template>

<script>
import { defineComponent, ref, computed, watch, onMounted, onBeforeUnmount, getCurrentInstance } from '@vue/composition-api'
import listingLayout from '@/components/ListingLayout'
import PageHeading from '@/components/PageHeading'
import ResponsiveTable from '@/components/ResponsiveTable'
import Pagination from '@/components/Pagination'
import modal from '@/components/Modal'
import AppTooltip from '@/components/AppTooltip'
import CommitteeSecretariesLink from '@/components/Committees/CommitteeSecretariesLink'
import ActionGateModal from '@/components/ActionGateModal'
import FilterBar from '@/components/filters/FilterBar'
import FilterMultiselect from '@/components/filters/FilterMultiselect'
import FilterCheckbox from '@/components/filters/FilterCheckbox'
import ListNameCell from '@/components/ListNameCell'
import useDraftsList, { acaYearStart } from '@/composables/drafts/useDraftsList'
import useActionGate from '@/composables/useActionGate'
import useDeleteDraft from '@/composables/drafts/useDeleteDraft'
import useRestoreDraft from '@/composables/drafts/useRestoreDraft'
import usePublishNominations from '@/composables/drafts/usePublishNominations'
import { DRAFT_STATUS_PUBLISHED, DRAFT_STATUS_PENDING, DRAFT_STATUS_POSTPONED } from '@/constants/draftStatus'
import { buildColumns, buildBreakpoints } from './columns'

// Keyed by id (DraftStatus enum, CAM-1503), not by the label text the API sends — a status name
// changing in the backend enum's label() no longer silently breaks the badge colour. Anything
// without an explicit colour (e.g. Cancelled) falls back to 'alert-danger' in statusBadgeClass().
const STATUS_BADGE = {
  [DRAFT_STATUS_PUBLISHED]: 'alert-success',
  [DRAFT_STATUS_PENDING]: 'alert-info',
  [DRAFT_STATUS_POSTPONED]: 'alert-warning'
}

// Second tag shown next to the status badge only while the "Open for Nominations" checkbox is on
// (CAM-1503) — mirrors the 3 states Nominations/DraftsList.vue distinguishes with a button
// (nominating now / entries closed but not yet Finalised / Finalise state = waitlist operating),
// computed server-side (Draft::nominationStage()) so the timezone-aware noms_closed comparison
// isn't duplicated on the frontend.
const NOMINATION_STAGE_BADGE = {
  nominating: { label: 'Open', badgeClass: 'alert-success' },
  finalising: { label: 'Being Finalised', badgeClass: 'alert-warning' },
  waitlist: { label: 'Waitlist', badgeClass: 'alert-info' }
}

export default defineComponent({
  name: 'DraftsList',
  props: ['success'],
  components: {
    'listing-layout': listingLayout,
    PageHeading,
    ResponsiveTable,
    Pagination,
    modal,
    AppTooltip,
    CommitteeSecretariesLink,
    ActionGateModal,
    FilterBar,
    FilterMultiselect,
    FilterCheckbox,
    ListNameCell
  },
  setup(props) {
    const { proxy } = getCurrentInstance()
    const store = proxy.$store

    const {
      rows, loading, page, totalPages, filters, perPageOptions,
      fetch, setPage, setSort, setPerPage, setSearch, setFilter, setFilters, clearFilter, clearFilters, syncFromQuery
    } = useDraftsList()

    const actionGate = useActionGate()
    const { runGated } = actionGate
    const { deleteDraft } = useDeleteDraft()
    const { restoreDraft } = useRestoreDraft()
    const { publish } = usePublishNominations({ runGated })

    const columns = buildColumns()
    const breakpoints = buildBreakpoints()

    // All + one tab per admin draft status. Unpublished / Deleted / Open for Nominations are now
    // independent checkboxes in the filter bar (CAM-1503), not tab values.
    const tabs = computed(() => {
      const statuses = store.getters.getDraftStatusesAdmin || []
      return [
        { value: 'all', label: 'All' },
        ...statuses.map(s => ({ value: String(s.id), label: s.name }))
      ]
    })

    const zoneOptions = computed(() => store.state.listZones || [])

    function statusBadgeClass(id) {
      return STATUS_BADGE[id] || 'alert-danger'
    }
    function isPublishedStatus(id) {
      return id === DRAFT_STATUS_PUBLISHED
    }
    function nominationStageBadge(row) {
      return NOMINATION_STAGE_BADGE[row.nomination_stage] || null
    }
    function formatDmy(value) {
      if (!value) return ''
      const d = new Date(value)
      if (Number.isNaN(d.getTime())) return ''
      return `${d.getDate()}/${d.getMonth() + 1}/${String(d.getFullYear()).slice(2)}`
    }

    const hasActiveFilters = computed(() =>
      Boolean(filters.search)
      || filters.tab !== 'all'
      || filters.from_date !== acaYearStart()
      || Boolean(filters.to_date)
      || filters.zone_id.length > 0
      || Boolean(filters.unpublished)
      || Boolean(filters.deleted)
      || Boolean(filters.open_for_nominations)
    )

    const searchInput = ref(filters.search || '')
    const alertMessage = ref('')
    const errorMessage = ref('')

    let searchTimer = null
    function applySearch() {
      clearTimeout(searchTimer)
      if (searchInput.value !== filters.search) setSearch(searchInput.value)
    }
    watch(searchInput, (val) => {
      clearTimeout(searchTimer)
      searchTimer = setTimeout(() => {
        if (val !== filters.search) setSearch(val)
      }, 500)
    })
    onBeforeUnmount(() => clearTimeout(searchTimer))

    function onTabChange(value) {
      setFilter('tab', value)
    }

    // Unpublished / Deleted / Open for Nominations are mutually exclusive (2026-09-14 revision):
    // each one narrows the list on its own (same as a tab would), so checking one clears the
    // other two rather than combining with them. setFilters() (not repeated setFilter() calls)
    // batches all 3 into a single syncQuery()/fetch() — setFilter() fetches on every call, which
    // was firing up to 3 requests for one click.
    const EXCLUSIVE_CHECKBOXES = ['unpublished', 'deleted', 'open_for_nominations']
    function setExclusiveCheckbox(name, value) {
      const updates = { [name]: value }
      if (value === '1') {
        EXCLUSIVE_CHECKBOXES.forEach((key) => {
          if (key !== name) updates[key] = ''
        })
      }
      setFilters(updates)
    }

    // "Unpublished" keeps the same password gate the legacy filterGuard applied to its tab —
    // only checking it (not unchecking) needs the gate.
    async function onUnpublishedToggle(value) {
      if (value === '1') {
        try {
          await runGated(() => true)
        } catch (_) {
          return
        }
      }
      setExclusiveCheckbox('unpublished', value)
    }

    function onDeletedToggle(value) {
      setExclusiveCheckbox('deleted', value)
    }

    function onOpenForNominationsToggle(value) {
      setExclusiveCheckbox('open_for_nominations', value)
    }

    function clearAll() {
      clearTimeout(searchTimer)
      searchInput.value = ''
      clearFilters()
    }

    // --- row actions ---
    const draftToDelete = ref(null)
    const publishingId = ref(null)
    const restoringId = ref(null)

    function askDelete(row) {
      draftToDelete.value = row
    }
    async function confirmDelete() {
      const row = draftToDelete.value
      draftToDelete.value = null
      const ok = await deleteDraft(row.id)
      if (ok) fetch()
    }
    async function publishDraft(row) {
      publishingId.value = row.id
      const ok = await publish(row.id)
      publishingId.value = null
      if (ok) fetch()
    }
    async function restoreDraftRow(row) {
      restoringId.value = row.id
      const ok = await restoreDraft(row.id)
      restoringId.value = null
      if (ok) fetch()
    }

    function loadPage() {
      const query = proxy.$route.query
      const success = props.success || query.success
      if (success) {
        alertMessage.value = success
        if (query.success) {
          const rest = { ...query }
          delete rest.success
          proxy.$router.replace({ query: rest }).catch(() => {})
        }
      }
      fetch()
    }

    onMounted(loadPage)

    // Re-load if the same component instance is reused for this route.
    watch(() => proxy.$route.path, () => {
      clearTimeout(searchTimer)
      syncFromQuery()
      searchInput.value = filters.search || ''
      alertMessage.value = ''
      errorMessage.value = ''
      loadPage()
    })

    return {
      ...actionGate,
      columns,
      breakpoints,
      tabs,
      zoneOptions,
      rows,
      loading,
      page,
      totalPages,
      filters,
      perPageOptions,
      searchInput,
      alertMessage,
      errorMessage,
      hasActiveFilters,
      draftToDelete,
      publishingId,
      restoringId,
      setPage,
      setSort,
      setPerPage,
      setFilter,
      clearFilter,
      applySearch,
      onTabChange,
      onUnpublishedToggle,
      onDeletedToggle,
      onOpenForNominationsToggle,
      clearAll,
      askDelete,
      confirmDelete,
      publishDraft,
      restoreDraftRow,
      statusBadgeClass,
      isPublishedStatus,
      nominationStageBadge,
      formatDmy
    }
  }
})
</script>

<style scoped>
/* Layout + the global `.badge { min-width: 58px }` reset live in ListNameCell; here we only
   need the status badge to line its check icon up with the label. */
.dl-status-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

/* Committee cell: the link is only on the name; the people icon jumps to Secretary Users
   pre-searched by this committee, so an admin can quickly find a secretary to log in as. */
.dl-committee {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

/* Keep the "Final" badge on the same line as the zone code — two bare sibling <span>s wrap the
   badge onto its own line as soon as the column is a bit narrow (see columns.js's Zone width). */
.dl-zone {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}

.dl-committee__name {
  text-wrap: pretty;
}
</style>



// WEBPACK FOOTER //
// src/views/Drafts/Listing/Drafts.vue